<template>
    <div class="byo-hosting-page">
        <header class="dataset-pass-header">
            <div>
                <p class="dataset-pass-kicker">BYO Hosting</p>
                <h1>{{ planHeading }}</h1>
                <p v-if="!isEnterprisePlan">
                    Create your account, set up a workspace, activate your BYO
                    hosting plan, and start uploading.
                </p>
                <p v-else>
                    This plan is available for enterprise customers. Please
                    contact your administrator or our enterprise team to
                    continue.
                </p>
            </div>
            <a class="dataset-pass-login" href="/accounts/login/">Sign in</a>
        </header>

        <div
            v-if="!isEnterprisePlan"
            class="dataset-pass-steps"
            aria-label="BYO hosting signup steps"
        >
            <div
                v-for="step in steps"
                :key="step.value"
                class="dataset-pass-step"
                :class="{
                    'dataset-pass-step--active': currentStep === step.value,
                    'dataset-pass-step--complete': currentStep > step.value
                }"
            >
                <span>{{ step.value }}</span>
                <strong>{{ step.title }}</strong>
            </div>
        </div>

        <template v-if="isEnterprisePlan">
            <section class="dataset-pass-grid dataset-pass-grid--enterprise">
                <v-card
                    class="dataset-pass-card dataset-pass-card--enterprise"
                    variant="outlined"
                >
                    <div class="dataset-pass-card__icon">
                        <v-icon>$vuetify.icons.billing</v-icon>
                    </div>
                    <h2>Contact your administrator or sales</h2>
                    <p>
                        Enterprise onboarding is handled by your administrator
                        or the SmartData enterprise team. An enterprise
                        onboarding specialist will contact you.
                    </p>

                    <div class="dataset-pass-enterprise-actions">
                        <v-btn
                            color="primary"
                            size="large"
                            variant="flat"
                            @click="contactSales"
                        >
                            Contact Sales
                        </v-btn>
                        <v-btn
                            color="primary"
                            size="large"
                            variant="tonal"
                            :href="
                                planSlug
                                    ? `/accounts/plan/select/?plan=${planSlug}`
                                    : '/accounts/plan/select/'
                            "
                        >
                            Review plans
                        </v-btn>
                    </div>

                    <v-alert
                        type="info"
                        variant="outlined"
                        class="dataset-pass-info dataset-pass-alert--info"
                    >
                        Contact your administrator if your organization already
                        has an enterprise onboarding process in place.
                    </v-alert>
                </v-card>

                <aside class="dataset-pass-summary">
                    <v-card variant="outlined" class="dataset-pass-summary-card">
                        <p class="dataset-pass-summary-label">Selected Plan</p>
                        <h3>{{ planName }}</h3>
                        <p class="dataset-pass-price">{{ planPrice }}</p>

                        <dl>
                            <div>
                                <dt>Hosted Dataset Capacity</dt>
                                <dd>{{ hostedDatasetCapacity }}</dd>
                            </div>
                            <div>
                                <dt>Storage Capacity</dt>
                                <dd>{{ storageCapacity }}</dd>
                            </div>
                            <div>
                                <dt>Team Seats</dt>
                                <dd>{{ teamSeatsCapacity }}</dd>
                            </div>
                            <div>
                                <dt>Max Teams</dt>
                                <dd>{{ maxTeamsCapacity }}</dd>
                            </div>
                            <div>
                                <dt>Billing Period</dt>
                                <dd>{{ billingPeriod }}</dd>
                            </div>
                        </dl>
                    </v-card>
                </aside>
            </section>
        </template>
        <template v-else>
            <v-alert
                v-if="errorMessage"
                type="error"
                variant="outlined"
                class="dataset-pass-alert dataset-pass-alert--error"
            >
                {{ errorMessage }}
            </v-alert>

            <section class="dataset-pass-grid">
                <v-card class="dataset-pass-card" variant="outlined">
                <template v-if="currentStep === 1">
                    <div class="dataset-pass-card__icon">
                        <v-icon>$vuetify.icons.profile</v-icon>
                    </div>
                    <h2>Create your account</h2>
                    <p>
                        Create your SmartData account and continue straight to
                        workspace setup. Email verification is sent in the
                        background.
                    </p>

                    <v-form ref="accountForm" @submit.prevent="submitAccount">
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="account.firstName"
                                    label="First Name"
                                    :rules="[rules.required]"
                                    variant="outlined"
                                    autocomplete="given-name"
                                    :error-messages="fieldErrors.first_name"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="account.lastName"
                                    label="Last Name"
                                    :rules="[rules.required]"
                                    variant="outlined"
                                    autocomplete="family-name"
                                    :error-messages="fieldErrors.last_name"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="account.email"
                                    label="Email"
                                    :rules="[rules.required, rules.email]"
                                    variant="outlined"
                                    autocomplete="email"
                                    :error-messages="fieldErrors.email"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="account.password1"
                                    label="Password"
                                    :rules="[rules.required]"
                                    :type="showPassword ? 'text' : 'password'"
                                    :append-inner-icon="
                                        showPassword
                                            ? '$vuetify.icons.eye'
                                            : '$vuetify.icons.eyeSlash'
                                    "
                                    variant="outlined"
                                    autocomplete="new-password"
                                    :error-messages="fieldErrors.password1"
                                    @click:append-inner="showPassword = !showPassword"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="account.password2"
                                    label="Confirm Password"
                                    :rules="[rules.required, rules.passwordMatch]"
                                    :type="showPassword ? 'text' : 'password'"
                                    variant="outlined"
                                    autocomplete="new-password"
                                    :error-messages="fieldErrors.password2"
                                />
                            </v-col>
                        </v-row>

                        <v-btn
                            block
                            color="primary"
                            size="large"
                            type="submit"
                            :loading="loadingAccount"
                            :disabled="!plan"
                        >
                            Create Account
                        </v-btn>
                    </v-form>
                </template>

                <template v-else-if="currentStep === 2">
                    <div class="dataset-pass-card__icon">
                        <v-icon>$vuetify.icons.billing</v-icon>
                    </div>
                    <h2>Set up your workspace</h2>
                    <p>
                        Your workspace contains your company, first team, and
                        BYO hosting billing account.
                    </p>

                    <v-form ref="workspaceForm" @submit.prevent="submitWorkspace">
                        <v-text-field
                            v-model="workspace.companyName"
                            label="Company Name"
                            :rules="[rules.required]"
                            variant="outlined"
                            autocomplete="organization"
                        />
                        <v-text-field
                            v-model="workspace.teamName"
                            label="Team Name"
                            :rules="[rules.required]"
                            variant="outlined"
                        />

                        <v-alert
                            type="info"
                            variant="outlined"
                            class="dataset-pass-info dataset-pass-alert--info"
                        >
                            We will create your company, team, and billing
                            account so you can get started right away.
                        </v-alert>

                        <v-btn
                            block
                            color="primary"
                            size="large"
                            type="submit"
                        >
                            Continue
                        </v-btn>
                    </v-form>
                </template>

                <template v-else-if="currentStep === 3">
                    <div class="dataset-pass-card__icon">
                        <v-icon>$vuetify.icons.lock</v-icon>
                    </div>
                    <h2>{{ activationHeading }}</h2>
                    <p>
                        {{ activationCopy }}
                    </p>

                    <div class="dataset-pass-review">
                        <div>
                            <span>Company</span>
                            <strong>{{ workspace.companyName }}</strong>
                        </div>
                        <div>
                            <span>Team</span>
                            <strong>{{ workspace.teamName }}</strong>
                        </div>
                        <div>
                            <span>Plan</span>
                            <strong>{{ planName }}</strong>
                        </div>
                    </div>

                    <template v-if="configurationError">
                        <v-alert
                            type="error"
                            variant="outlined"
                            class="dataset-pass-info dataset-pass-alert--error"
                        >
                            {{ configurationErrorMessage }}
                        </v-alert>

                        <v-btn
                            block
                            color="primary"
                            size="large"
                            variant="tonal"
                            @click="contactSales"
                        >
                            Contact Sales
                        </v-btn>
                    </template>
                    <template v-else-if="usesStripePayment && paymentReady">
                        <v-alert
                            type="info"
                            variant="outlined"
                            class="dataset-pass-info dataset-pass-alert--info"
                        >
                            Your workspace is ready. Add payment to activate the
                            billing account and continue to upload.
                        </v-alert>

                        <stripe-form
                            :billing-info="billingInfo"
                            :success-redirect-url="uploadUrl"
                            :submit-label="paymentSubmitLabel"
                            :use-assigned-plan="true"
                        />
                    </template>
                    <v-btn
                        v-else
                        block
                        color="primary"
                        size="large"
                        :loading="loadingActivation"
                        :disabled="!plan"
                        @click="activateDatasetPass"
                    >
                        {{ activationSubmitLabel }}
                    </v-btn>
                </template>

                <template v-else>
                    <div class="dataset-pass-ready-icon">
                        <v-icon>$vuetify.icons.complete</v-icon>
                    </div>
                    <h2 v-if="!manualInvoicePending && !configurationError">
                        Your {{ planName }} workspace is ready.
                    </h2>
                    <h2 v-else-if="manualInvoicePending">
                        {{ planName }} is awaiting manual invoice approval.
                    </h2>
                    <h2 v-else>
                        There is a configuration issue with {{ planName }}.
                    </h2>
                    <p v-if="configurationError">
                        We could not continue because this plan is missing
                        Stripe checkout configuration.
                    </p>
                    <p v-else-if="!manualInvoicePending">
                        Start uploading your first dataset.
                    </p>
                    <p v-else-if="manualInvoicePending">
                        We created your workspace and billing account. Uploads
                        will be enabled once the invoice is approved.
                    </p>
                    <p v-else>
                        Please contact support to resolve the Stripe
                        configuration for this plan.
                    </p>

                    <div class="dataset-pass-ready-list">
                        <div>
                            <span>Team</span>
                            <strong>{{ activationTeamName }}</strong>
                        </div>
                        <div>
                            <span>Billing Account</span>
                            <strong>{{ activationBillingAccountName }}</strong>
                        </div>
                        <div>
                            <span>Selected Plan</span>
                            <strong>{{ planName }}</strong>
                        </div>
                    </div>

                    <v-alert
                        type="success"
                        variant="outlined"
                        class="dataset-pass-info dataset-pass-alert--success"
                        v-if="!manualInvoicePending && !configurationError"
                    >
                        Ready to go. Upload your first dataset and we will take
                        care of the rest.
                    </v-alert>
                    <v-alert
                        v-else-if="manualInvoicePending"
                        type="info"
                        variant="outlined"
                        class="dataset-pass-info dataset-pass-alert--info"
                    >
                        We will email you when manual activation is complete.
                    </v-alert>

                    <v-btn
                        v-if="!manualInvoicePending && !configurationError"
                        block
                        color="primary"
                        size="large"
                        @click="startUploading"
                    >
                        Start Uploading
                    </v-btn>
                </template>
                </v-card>

            <aside class="dataset-pass-summary">
                <v-card variant="outlined" class="dataset-pass-summary-card">
                    <p class="dataset-pass-summary-label">Selected Plan</p>
                    <h3>{{ planName }}</h3>
                    <p class="dataset-pass-price">{{ planPrice }}</p>

                    <dl>
                        <div>
                            <dt>Hosted Dataset Capacity</dt>
                            <dd>{{ hostedDatasetCapacity }}</dd>
                        </div>
                        <div>
                            <dt>Storage Capacity</dt>
                            <dd>{{ storageCapacity }}</dd>
                        </div>
                        <div>
                            <dt>Team Seats</dt>
                            <dd>{{ teamSeatsCapacity }}</dd>
                        </div>
                        <div>
                            <dt>Max Teams</dt>
                            <dd>{{ maxTeamsCapacity }}</dd>
                        </div>
                        <div>
                            <dt>Billing Period</dt>
                            <dd>{{ billingPeriod }}</dd>
                        </div>
                    </dl>
                </v-card>
            </aside>
        </section>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import StripeForm from 'components/Stripe/StripeForm/StripeForm.vue';

function parseFieldErrors(errorsJson) {
    if (!errorsJson) return {};
    try {
        const errors = JSON.parse(errorsJson);
        return Object.keys(errors).reduce((parsed, key) => {
            parsed[key] = errors[key].map((error) => error.message);
            return parsed;
        }, {});
    } catch {
        return {};
    }
}

export default {
    name: 'BYOHostingSignupFlow',
    components: { StripeForm },
    props: {
        plan: {
            type: Object,
            default: function() {
                return null;
            }
        },
        requestedPlanSlug: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            currentStep: 1,
            loadingAccount: false,
            loadingActivation: false,
            paymentReady: false,
            manualInvoicePending: false,
            billingInfo: null,
            errorMessage: '',
            fieldErrors: {},
            showPassword: false,
            account: {
                firstName: '',
                lastName: '',
                email: '',
                password1: '',
                password2: ''
            },
            workspace: {
                companyName: '',
                teamName: ''
            },
            activation: {
                team: null,
                billingAccount: null,
                uploadUrl: ''
            },
            rules: {
                required: (value) => !!value || 'Required field',
                email: (value) => {
                    const pattern = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-A-Za-z]+\.)+[A-Za-z]{2,}))$/;
                    return pattern.test(value) || 'Must be a valid e-mail';
                },
                passwordMatch: () =>
                    this.account.password1 === this.account.password2 ||
                    'Passwords must match'
            }
        };
    },
    computed: {
        isEnterprisePlan() {
            return Boolean(this.plan?.is_enterprise);
        },
        planSlug() {
            return this.requestedPlanSlug || this.plan?.slug || '';
        },
        planHeading() {
            return this.plan?.name
                ? `Create your ${this.planName} account`
                : 'Create your BYO hosting account';
        },
        steps() {
            return [
                { value: 1, title: 'Create Account' },
                { value: 2, title: 'Set Up Workspace' },
                { value: 3, title: 'Activate Hosting' },
                { value: 4, title: "You're Ready" }
            ];
        },
        byoHosting() {
            return this.plan?.byo_hosting || {};
        },
        planLimits() {
            return this.byoHosting?.limits || {};
        },
        planName() {
            return this.plan?.name || 'Selected BYO hosting plan';
        },
        planPrice() {
            if (!this.plan) return 'Loading plan';
            const currency = this.plan.currency || 'AUD';
            const cost = Number(this.plan.cost || 0);
            if (cost <= 0) return this.isEnterprisePlan ? 'Custom' : 'Free';
            return `${currency} ${cost} / ${this.billingPeriod.toLowerCase()}`;
        },
        stripeSubscriptionId() {
            const stripeInfo = this.byoHosting?.stripe || {};
            const stripePriceId = String(stripeInfo.stripe_price_id || '').trim();
            const stripePlanId = String(stripeInfo.stripe_plan_id || '').trim();
            if (stripePriceId && stripePriceId !== '-1') {
                return stripePriceId;
            }
            if (stripePlanId && stripePlanId !== '-1') {
                return stripePlanId;
            }
            return '';
        },
        stripeConfigurationValid() {
            return Boolean(this.stripeSubscriptionId);
        },
        configurationError() {
            return (
                this.paymentRequired &&
                !this.isEnterprisePlan &&
                !this.stripeConfigurationValid
            );
        },
        configurationErrorMessage() {
            return 'This BYO hosting plan is not configured for Stripe checkout. Please contact support.';
        },
        hostedDatasetCapacity() {
            return this.formatLimit(
                this.planLimits.included_hosted_datasets,
                'Hosted Dataset'
            );
        },
        storageCapacity() {
            const storageGb = this.planLimits.included_storage_gb;
            if (storageGb === -1) return 'Unlimited storage';
            if (storageGb === null || storageGb === undefined) {
                return 'Configured by plan';
            }
            return `${storageGb} GB`;
        },
        teamSeatsCapacity() {
            return this.formatLimit(
                this.planLimits.included_team_seats,
                'Team Seat'
            );
        },
        maxTeamsCapacity() {
            return this.formatLimit(
                this.plan?.limit?.team?.max,
                'Team'
            );
        },
        billingPeriod() {
            const frequency = Number(this.plan?.frequency || 0);
            if (frequency === 12) return 'Annual';
            if (frequency === 3) return 'Quarterly';
            return 'Monthly';
        },
        paymentRequired() {
            return Number(this.plan?.cost || 0) > 0;
        },
        activationHeading() {
            if (this.usesStripePayment) {
                return `Activate ${this.planName}`;
            }
            return `Activate ${this.planName}`;
        },
        usesStripePayment() {
            return this.stripeConfigurationValid;
        },
        activationCopy() {
            if (this.usesStripePayment) {
                return 'Activating creates your billing account and links it to your new team. Payment is required before upload access is enabled.';
            }
            return 'Activating creates your billing account, links it to your new team, and sends you straight to upload.';
        },
        activationSubmitLabel() {
            return this.usesStripePayment
                ? 'Create Workspace and Continue to Payment'
                : 'Activate Hosting';
        },
        paymentSubmitLabel() {
            return `Pay ${this.planPrice}`;
        },
        uploadUrl() {
            return this.activation.uploadUrl || '/upload';
        },
        activationTeamName() {
            return this.activation.team?.name || '';
        },
        activationBillingAccountName() {
            return this.activation.billingAccount?.name || '';
        }
    },
    mounted() {
        const urlParameters = new URLSearchParams(window.location.search);
        this.account.email = urlParameters.get('email') || '';
    },
    methods: {
        async validateForm(refName) {
            const form = this.$refs[refName];
            if (!form) return false;
            const result = await form.validate();
            return typeof result === 'boolean' ? result : Boolean(result?.valid);
        },
        formatLimit(value, label) {
            if (value === -1) return `Unlimited ${label}s`;
            if (value === null || value === undefined) return 'Configured by plan';
            const count = Number(value);
            return `${count} ${label}${count === 1 ? '' : 's'}`;
        },
        async submitAccount() {
            if (!this.plan) {
                this.errorMessage =
                    'This BYO hosting plan is currently unavailable. Please contact support.';
                return;
            }
            if (!(await this.validateForm('accountForm'))) return;

            this.loadingAccount = true;
            this.errorMessage = '';
            this.fieldErrors = {};
            try {
                const response = await axios.post(
                    '/api/accounts/dataset-pass/register/',
                    {
                        first_name: this.account.firstName,
                        last_name: this.account.lastName,
                        email: this.account.email,
                        password1: this.account.password1,
                        password2: this.account.password2,
                        countryCode: '',
                        phoneNumber: '',
                        requestedPlanSlug: this.planSlug
                    }
                );

                if (!response.data.success) {
                    this.fieldErrors = parseFieldErrors(response.data.errors);
                    this.errorMessage =
                        response.data.message ||
                        'Please check your account details and try again.';
                    return;
                }

                this.workspace.companyName = [
                    this.account.firstName,
                    this.account.lastName
                ]
                    .filter(Boolean)
                    .join(' ');
                this.workspace.teamName = 'Operations Team';
                this.currentStep = 2;
            } catch (error) {
                this.errorMessage =
                    error?.response?.data?.message ||
                    'There was an error creating your BYO hosting account.';
            } finally {
                this.loadingAccount = false;
            }
        },
        async submitWorkspace() {
            if (!(await this.validateForm('workspaceForm'))) return;
            this.currentStep = 3;
        },
        async activateDatasetPass() {
            if (!this.plan) {
                this.errorMessage =
                    'This BYO hosting plan is currently unavailable. Please contact support.';
                return;
            }
            if (this.configurationError) {
                this.errorMessage = this.configurationErrorMessage;
                return;
            }
            this.loadingActivation = true;
            this.errorMessage = '';
            try {
                const response = await axios.post(
                    '/api/accounts/dataset-pass/activate/',
                    {
                        requestedPlanSlug: this.planSlug,
                        companyName: this.workspace.companyName,
                        teamName: this.workspace.teamName
                    }
                );

                if (!response.data.success) {
                    this.errorMessage =
                        response.data.message ||
                        'There was an error activating your BYO hosting plan.';
                    return;
                }

                this.activation = {
                    team: response.data.team,
                    billingAccount: response.data.billingAccount,
                    uploadUrl: response.data.uploadUrl
                };
                this.billingInfo = response.data.billingInfo;
                if (response.data.manualInvoice) {
                    this.manualInvoicePending = true;
                    this.currentStep = 4;
                    return;
                }
                if (response.data.requiresPayment) {
                    this.paymentReady = true;
                    return;
                }
                this.currentStep = 4;
                window.location.href = response.data.uploadUrl;
            } catch (error) {
                this.errorMessage =
                    error?.response?.data?.message ||
                    'There was an error activating your BYO hosting plan.';
            } finally {
                this.loadingActivation = false;
            }
        },
        startUploading() {
            window.location.href = this.activation.uploadUrl || '/upload';
        },
        contactSales() {
            const message = `Hi SmartData team, I'd like help with the enterprise plan ${this.planName}.`;
            if (window.Intercom) {
                window.Intercom('showNewMessage', message);
                return;
            }

            window.location.href = this.planSlug
                ? `/accounts/plan/select/?plan=${this.planSlug}`
                : '/accounts/plan/select/';
        }
    }
};
</script>

<style scoped>
.byo-hosting-page {
    width: min(1280px, 100%);
    color: var(--sd-text);
    --byo-surface: var(--sd-surface);
    --byo-surface-soft: var(--sd-surface-soft);
    --byo-surface-panel: var(--sd-surface-panel);
    --byo-bg: var(--sd-bg);
    --byo-border: var(--sd-border);
    --byo-border-soft: var(--sd-border-subtle);
    --byo-text: var(--sd-text);
    --byo-text-secondary: var(--sd-text-secondary);
    --byo-text-muted: var(--sd-text-muted);
    --byo-primary: var(--sd-primary);
    --byo-primary-surface: var(--sd-primary-surface);
    --byo-primary-surface-soft: var(--sd-primary-surface-soft-alpha);
    --byo-primary-border: var(--sd-primary-border-muted);
    --byo-success-surface: var(--sd-success-surface-alpha);
    --byo-success-border: var(--sd-success-border-alpha);
    --byo-error-surface: var(--sd-error-surface-alpha);
    --byo-error-border: var(--sd-error-border-alpha);
    --byo-shadow: var(--sd-shadow-soft);
}

.theme--dark .byo-hosting-page,
.v-theme--dark .byo-hosting-page,
body.theme--dark .byo-hosting-page {
    --byo-shadow: var(--sd-shadow-soft);
}

.dataset-pass-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 22px;
}

.dataset-pass-kicker {
    margin: 0 0 6px;
    color: var(--byo-primary);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.dataset-pass-header h1 {
    margin: 0;
    color: var(--byo-text);
    font-size: clamp(2rem, 4vw, 3.4rem);
    line-height: 1.05;
}

.dataset-pass-header p {
    max-width: 620px;
    margin: 10px 0 0;
    color: var(--byo-text-secondary);
    font-size: 1.05rem;
}

.dataset-pass-login {
    color: var(--byo-primary);
    font-weight: 700;
    text-decoration: none;
}

.dataset-pass-login:hover {
    text-decoration: underline;
}

.dataset-pass-steps {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 20px;
}

.dataset-pass-step {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    color: var(--byo-text-secondary);
}

.dataset-pass-step span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--byo-primary-surface);
    color: var(--byo-primary);
    font-weight: 800;
}

.dataset-pass-step strong {
    overflow-wrap: anywhere;
}

.dataset-pass-step--active,
.dataset-pass-step--complete {
    color: var(--byo-text);
}

.dataset-pass-step--active span,
.dataset-pass-step--complete span {
    background: var(--byo-primary);
    color: var(--sd-text-inverse);
}

.dataset-pass-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 24px;
    align-items: start;
}

.dataset-pass-grid--enterprise {
    grid-template-columns: minmax(0, 1fr) 320px;
}

.dataset-pass-card,
.dataset-pass-summary-card {
    border-color: var(--byo-border) !important;
    border-radius: 14px !important;
    background: var(--byo-surface) !important;
    color: var(--byo-text) !important;
    box-shadow: var(--byo-shadow) !important;
}

.dataset-pass-card {
    padding: 34px;
}

.dataset-pass-card--enterprise {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 34px;
}

.dataset-pass-card__icon,
.dataset-pass-ready-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    margin-bottom: 18px;
    border-radius: 50%;
    background: var(--byo-primary-surface);
    color: var(--byo-primary);
    font-size: 28px;
}

.dataset-pass-ready-icon {
    background: var(--sd-success-surface-alpha);
    color: var(--sd-success);
}

.dataset-pass-card h2 {
    margin: 0 0 8px;
    color: var(--byo-text);
    font-size: 1.65rem;
}

.dataset-pass-card p {
    max-width: 620px;
    margin: 0 0 24px;
    color: var(--byo-text-secondary);
}

.dataset-pass-enterprise-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.dataset-pass-enterprise-actions :deep(.v-btn) {
    min-width: 180px;
}

.dataset-pass-info {
    margin: 10px 0 20px;
}

.dataset-pass-alert {
    margin-bottom: 16px;
}

.dataset-pass-alert--error {
    border-color: var(--byo-error-border) !important;
    background: var(--byo-error-surface) !important;
    color: var(--byo-text) !important;
}

.dataset-pass-alert--info {
    border-color: var(--byo-primary-border) !important;
    background: var(--byo-primary-surface-soft) !important;
    color: var(--byo-text) !important;
}

.dataset-pass-alert--success {
    border-color: var(--byo-success-border) !important;
    background: var(--byo-success-surface) !important;
    color: var(--byo-text) !important;
}

.dataset-pass-summary-card {
    padding: 24px;
}

.dataset-pass-summary-label {
    margin: 0 0 8px;
    color: var(--byo-text-muted);
    font-size: 0.85rem;
    font-weight: 700;
    text-transform: uppercase;
}

.dataset-pass-summary-card h3 {
    margin: 0;
    color: var(--byo-text);
}

.dataset-pass-price {
    margin: 8px 0 20px;
    color: var(--byo-primary);
    font-weight: 800;
}

.dataset-pass-summary-card dl,
.dataset-pass-ready-list,
.dataset-pass-review {
    display: grid;
    gap: 14px;
}

.dataset-pass-summary-card dl {
    margin: 0;
}

.dataset-pass-summary-card dl div,
.dataset-pass-ready-list div,
.dataset-pass-review div {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--byo-border-soft);
}

.dataset-pass-summary-card dt,
.dataset-pass-ready-list span,
.dataset-pass-review span {
    color: var(--byo-text-secondary);
    font-weight: 700;
}

.dataset-pass-summary-card dd,
.dataset-pass-ready-list strong,
.dataset-pass-review strong {
    margin: 0;
    color: var(--byo-text);
    text-align: right;
}

.byo-hosting-page :deep(.v-field) {
    background: var(--byo-surface-panel);
    border-radius: 12px;
}

.byo-hosting-page :deep(.v-field__input),
.byo-hosting-page :deep(.v-label),
.byo-hosting-page :deep(.v-messages__message) {
    color: var(--byo-text);
}

.byo-hosting-page :deep(.v-field__field input) {
    color: var(--byo-text);
}

@media (max-width: 960px) {
    .dataset-pass-steps,
    .dataset-pass-grid {
        grid-template-columns: 1fr;
    }

    .dataset-pass-header {
        flex-direction: column;
    }

    .dataset-pass-card {
        padding: 24px;
    }
}
</style>
